import { useCallback, useEffect, useState } from 'react';
import { FileText, Plus, RefreshCw, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import DataTable, { type Column } from '../components/DataTable';
import Tabs from '../components/Tabs';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import FilterBar, {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import OpenInAppButton from '../components/OpenInAppButton';
import { formatCurrency, formatDate } from '../lib/format';
import { fetchInvoices, fetchUnpaidInvoices, createInvoice, createInvoicePayLink } from '../lib/api';

type Row = Record<string, unknown>;
type TabId = 'all' | 'unpaid';

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return null;
}

function coerceList(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as Row[];
    if (obj.data && typeof obj.data === 'object' && Array.isArray((obj.data as Row).data)) {
      return (obj.data as Row).data as Row[];
    }
  }
  return [];
}

const columns: Column<Row>[] = [
  { key: 'number', header: 'Invoice', render: (r) => str(r, 'number', 'invoice_number', 'id') ?? '—' },
  { key: 'client', header: 'Client', render: (r) => str(r, 'client_name', 'client_id') ?? '—' },
  { key: 'date', header: 'Date', render: (r) => formatDate(str(r, 'date', 'created_at')) },
  { key: 'due_date', header: 'Due', render: (r) => formatDate(str(r, 'due_date')) },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (r) => formatCurrency(str(r, 'amount', 'total'), str(r, 'currency') ?? 'USD'),
  },
  {
    key: 'balance',
    header: 'Balance',
    align: 'right',
    render: (r) => formatCurrency(str(r, 'balance'), str(r, 'currency') ?? 'USD'),
  },
  { key: 'status', header: 'Status', render: (r) => <StatusPill status={str(r, 'status') ?? 'draft'} /> },
  {
    key: 'pay',
    header: 'Pay',
    render: (r) => {
      const id = str(r, 'id', 'number');
      const balance = str(r, 'balance');
      if (!id || !balance || Number.parseFloat(balance) <= 0) return '—';
      return (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-zt-primary hover:text-zt-accent"
          onClick={async (e) => {
            e.stopPropagation();
            const result = await createInvoicePayLink(id);
            if (result.ok && result.data?.pay_url) {
              await navigator.clipboard.writeText(result.data.pay_url);
              toast.success('Pay link copied');
            }
          }}
        >
          <Link2 size={14} /> Copy link
        </button>
      );
    },
  },
];

function Invoices() {
  const [tab, setTab] = useState<TabId>('all');
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    if (tab === 'unpaid') {
      const result = await fetchUnpaidInvoices(undefined, 100, 0);
      if (result.ok && result.data) {
        setRows(result.data.data ?? []);
        setTotal(result.data.total ?? result.data.data?.length ?? 0);
      } else {
        setRows([]);
        setTotal(0);
      }
    } else {
      const result = await fetchInvoices(50);
      const list = result.ok && result.data ? coerceList(result.data.data) : [];
      setRows(list);
      setTotal(list.length);
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageShell
      title="Invoices"
      subtitle="Invoices synced from SolidInvoice. Create new invoices and track unpaid balances."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={tab === 'unpaid' ? 'Unpaid' : 'Invoices'} value={loading ? '…' : String(total)} tone={tab === 'unpaid' ? 'warning' : 'info'} icon={<FileText size={18} />} />
        <StatCard label="On page" value={loading ? '…' : String(rows.length)} tone="neutral" />
        <StatCard label="View" value={tab === 'unpaid' ? 'Unpaid' : 'All'} tone="neutral" />
        <StatCard label="Source" value="SolidInvoice" tone="neutral" />
      </div>

      <div className="flex items-center justify-between">
        <Tabs
          tabs={[
            { id: 'all', label: 'All' },
            { id: 'unpaid', label: 'Unpaid' },
          ]}
          active={tab}
          onChange={(id) => setTab(id as TabId)}
        />
      </div>

      <FilterBar
        actions={
          <>
            <OpenInAppButton integrationKey="solidinvoice" />
            <button type="button" onClick={load} className={secondaryButtonClass}>
              <RefreshCw size={16} /> Refresh
            </button>
            <button type="button" onClick={() => setCreateOpen(true)} className={primaryButtonClass}>
              <Plus size={16} /> New invoice
            </button>
          </>
        }
      >
        <span className="text-sm text-zt-muted">
          {tab === 'unpaid' ? 'Invoices with an outstanding balance' : 'All invoices'}
        </span>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r, i) => str(r, 'id', 'number') ?? String(i)}
        loading={loading}
        emptyTitle={tab === 'unpaid' ? 'No unpaid invoices' : 'No invoices'}
        emptyMessage="Connect SolidInvoice or create a new invoice to get started."
        emptyIcon={<FileText size={32} />}
        emptyAction={
          <button type="button" onClick={() => setCreateOpen(true)} className={primaryButtonClass}>
            <Plus size={16} /> New invoice
          </button>
        }
      />

      <CreateInvoiceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />
    </PageShell>
  );
}

function CreateInvoiceModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [clientId, setClientId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!clientId.trim() || !amount.trim()) {
      toast.error('Client ID and amount are required');
      return;
    }
    setSubmitting(true);
    const result = await createInvoice({
      client_id: clientId.trim(),
      amount: amount.trim(),
      currency,
      description: description.trim() || undefined,
      due_date: dueDate || undefined,
    });
    setSubmitting(false);
    if (result.ok) {
      toast.success('Invoice created');
      const created = result.data?.data as Row | Row[] | undefined;
      const row = Array.isArray(created) ? created[0] : created;
      const invoiceId = row && str(row, 'id');
      if (invoiceId) {
        const link = await createInvoicePayLink(invoiceId);
        if (link.ok && link.data?.pay_url) {
          await navigator.clipboard.writeText(link.data.pay_url);
          toast.success('Pay link copied to clipboard');
        }
      }
      setClientId('');
      setAmount('');
      setDescription('');
      setDueDate('');
      onClose();
      onCreated();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New invoice"
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Creating…' : 'Create invoice'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Client ID</label>
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="client-123"
            className={`w-full ${controlClass}`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Amount</label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="500.00"
              className={`w-full ${controlClass}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Currency</label>
            <input
              type="text"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className={`w-full ${controlClass}`}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Due date (optional)</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={`w-full ${controlClass}`}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`w-full ${controlClass}`}
          />
        </div>
      </div>
    </Modal>
  );
}

export default Invoices;
